import React, { useRef } from 'react';

import { useRouter } from 'next/router';

import { loadRewrites } from '@techstyle/next-routes';
import { useSession } from '@techstyle/redux-core';

import { Component as ErrorPageLayout } from '../components/ErrorPageLayout';
import { ErrorTemplate } from '../components/ErrorTemplate/ErrorTemplate';
import { Component as Loading } from '../components/Loading';

import { getBuilderJson } from './getBuilderJson';
import { isBuilderPreview } from './isBuilderPreview';
import { getPathname } from './url';

export default function builderPageIfContent({
  modelName,
  builderPage: BuilderPage,
  fallbackPage: FallbackPage,
}) {
  const WrapperPage = props => {
    const { isLoggedIn } = useSession();
    const prevIsLoggedIn = useRef(isLoggedIn);
    const router = useRouter();

    // If login status changed, navigates to the same page to force calling
    // getInitialProps, which will check if there's a Builder page targeted at
    // the new login status.
    if (prevIsLoggedIn.current !== isLoggedIn) {
      prevIsLoggedIn.current = isLoggedIn;
      router.replace(router.pathname, router.asPath);
      return <Loading />;
    }

    if (props.isNonExistingProductListingRoute) {
      return (
        <ErrorTemplate>
          <ErrorPageLayout statusCode={404} />
        </ErrorTemplate>
      );
    }

    return props.matchesBuilderDynamicRoute ? (
      <BuilderPage {...props} />
    ) : (
      <FallbackPage {...props} />
    );
  };

  WrapperPage.getInitialProps = async ctx => {
    const path = getPathname(ctx.asPath);
    const { builderJson } = await getBuilderJson(
      { modelName, urlPath: path },
      ctx
    );
    // When `isBuilderPreview`, getBuilderJson response includes
    // unpublished pages, see
    // getBuilderJson > getBuilderPageTargeting > includeUnpublished.
    // Because of this, when editing a Section model in Builder, through a
    // Preview URL that doesn't correspond to a Builder page, getBuilderJson
    // still returns page content for that URL path, but with the 'published'
    // property set to 'draft' instead of 'published'.
    const isBuilderPage = isBuilderPreview(ctx.req)
      ? ctx.req?.query['builder.preview'] === 'page' ||
        builderJson?.published === 'published'
      : !!builderJson?.data?.blocks;

    if (isBuilderPage) {
      const props = await BuilderPage.getInitialProps(ctx);
      return {
        ...props,
        matchesBuilderDynamicRoute: true,
      };
    }

    await ctx.store.dispatch(loadRewrites([path]));
    if (
      ['/features/', '/products/'].some(p => path.startsWith(p)) &&
      // If a product listing route with this path exists outside Builder, there
      // must be an entry in routeTypeByPath with the value 'PRODUCT_LISTING'.
      ctx.store.getState().dynamicRoutes.routeTypeByPath[path] !==
        'PRODUCT_LISTING'
    ) {
      return { isNonExistingProductListingRoute: true };
    }

    return FallbackPage.getInitialProps
      ? FallbackPage.getInitialProps(ctx)
      : {};
  };

  return WrapperPage;
}
